import React, { useState } from 'react';
import {
    List,
    Datagrid,
    TextField,
    DateField,
    BooleanField,
    EditButton,
    ShowButton,
    Create,
    Edit,
    Show,
    SimpleForm,
    TextInput,
    BooleanInput,
    DateTimeInput,
    SelectInput,
    ImageInput,
    ImageField,

    required,
    maxLength,
    minLength
} from 'react-admin';
import { Card, CardContent, Typography, Box, Chip, Grid } from '@mui/material';
import { RichTextInput } from 'ra-input-rich-text';

// List component for blog posts
export const BlogPostList = () => (
    <List>
        <Datagrid>
            <TextField source="title" />
            <TextField source="category" />
            <TextField source="apartment" />
            <BooleanField source="is_published" />
            <DateField source="published_date" />
            <DateField source="created_at" />
            <EditButton />
            <ShowButton />
        </Datagrid>
    </List>
);

// Create component for blog posts
export const BlogPostCreate = () => (
    <Create>
        <SimpleForm>
            <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                    <TextInput source="title" validate={[required(), maxLength(200)]} fullWidth />
                    <TextInput source="slug" validate={[required(), maxLength(100)]} fullWidth />
                    <SelectInput 
                        source="category" 
                        choices={[
                            { id: 'apartment', name: 'O apartamencie' },
                            { id: 'attractions', name: 'Atrakcje' },
                            { id: 'restaurants', name: 'Restauracje' },
                            { id: 'activities', name: 'Aktywności' },
                            { id: 'tips', name: 'Porady' },
                            { id: 'news', name: 'Aktualności' }
                        ]}
                        validate={required()}
                        fullWidth
                    />
                    <TextInput source="apartment" label="Apartment ID" fullWidth />
                </Grid>
                <Grid item xs={12} md={4}>
                    <BooleanInput source="is_published" />
                    <DateTimeInput source="published_date" />
                </Grid>
            </Grid>
            
            <ImageInput source="featured_image" label="Featured Image" accept="image/*">
                <ImageField source="src" title="title" />
            </ImageInput>
            
            <TextInput 
                source="short_description" 
                multiline 
                rows={3}
                validate={[maxLength(500)]}
                fullWidth
            />
            
            <RichTextInput 
                source="content" 
                label="Content"
                toolbar={[
                    ['bold', 'italic', 'underline', 'strikethrough'],
                    ['fontSize', 'color'],
                    ['justifyLeft', 'justifyCenter', 'justifyRight', 'justifyFull'],
                    ['insertUnorderedList', 'insertOrderedList'],
                    ['createLink', 'unlink'],
                    ['insertImage'],
                    ['removeFormat']
                ]}
            />
            
            <TextInput 
                source="meta_description" 
                multiline 
                rows={2}
                validate={[maxLength(160)]}
                fullWidth
                helperText="SEO meta description (max 160 characters)"
            />
        </SimpleForm>
    </Create>
);

// Edit component for blog posts
export const BlogPostEdit = () => (
    <Edit>
        <SimpleForm>
            <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                    <TextInput source="title" validate={[required(), maxLength(200)]} fullWidth />
                    <TextInput source="slug" validate={[required(), maxLength(100)]} fullWidth />
                    <SelectInput 
                        source="category" 
                        choices={[
                            { id: 'apartment', name: 'O apartamencie' },
                            { id: 'attractions', name: 'Atrakcje' },
                            { id: 'restaurants', name: 'Restauracje' },
                            { id: 'activities', name: 'Aktywności' },
                            { id: 'tips', name: 'Porady' },
                            { id: 'news', name: 'Aktualności' }
                        ]}
                        validate={required()}
                        fullWidth
                    />
                    <TextInput source="apartment" label="Apartment ID" fullWidth />
                </Grid>
                <Grid item xs={12} md={4}>
                    <BooleanInput source="is_published" />
                    <DateTimeInput source="published_date" />
                </Grid>
            </Grid>
            
            <ImageInput source="featured_image" label="Featured Image" accept="image/*">
                <ImageField source="src" title="title" />
            </ImageInput>
            
            <TextInput 
                source="short_description" 
                multiline 
                rows={3}
                validate={[maxLength(500)]}
                fullWidth
            />
            
            <RichTextInput 
                source="content" 
                label="Content"
                toolbar={[
                    ['bold', 'italic', 'underline', 'strikethrough'],
                    ['fontSize', 'color'],
                    ['justifyLeft', 'justifyCenter', 'justifyRight', 'justifyFull'],
                    ['insertUnorderedList', 'insertOrderedList'],
                    ['createLink', 'unlink'],
                    ['insertImage'],
                    ['removeFormat']
                ]}
            />
            
            <TextInput 
                source="meta_description" 
                multiline 
                rows={2}
                validate={[maxLength(160)]}
                fullWidth
                helperText="SEO meta description (max 160 characters)"
            />
        </SimpleForm>
    </Edit>
);

// Show component for blog posts
export const BlogPostShow = () => (
    <Show>
        <Card>
            <CardContent>
                <Typography variant="h4" gutterBottom>
                    <TextField source="title" />
                </Typography>
                
                <Box mb={2}>
                    <Chip 
                        label={<TextField source="category" />} 
                        color="primary" 
                        sx={{ mr: 1 }} 
                    />
                    <BooleanField source="is_published" />
                </Box>
                
                <Grid container spacing={2} mb={2}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">
                            Apartment
                        </Typography>
                        <TextField source="apartment" />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">
                            Published Date
                        </Typography>
                        <DateField source="published_date" />
                    </Grid>
                </Grid>
                
                <Box mb={2}>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                        Featured Image
                    </Typography>
                    <ImageField source="featured_image" />
                </Box>
                
                <Box mb={2}>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                        Short Description
                    </Typography>
                    <TextField source="short_description" />
                </Box>
                
                <Box mb={2}>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                        Content
                    </Typography>
                    <div dangerouslySetInnerHTML={{ __html: '<div id="content-preview"></div>' }} />
                    <script>
                        {`
                            document.getElementById('content-preview').innerHTML = record.content || '';
                        `}
                    </script>
                </Box>
                
                <Box mb={2}>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                        Meta Description
                    </Typography>
                    <TextField source="meta_description" />
                </Box>
                
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">
                            Created At
                        </Typography>
                        <DateField source="created_at" />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">
                            Updated At
                        </Typography>
                        <DateField source="updated_at" />
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    </Show>
); 